import { type CSSProperties } from 'react';

/* =============================================================================
   §17.2.c — Fondo / tema del espacio de trabajo ("vida visual").
   Librería CURADA de patrones, implementados en CSS/SVG ligero (sin imágenes
   pesadas). Se renderiza DETRÁS del contenido a baja opacidad: las cards/tablas
   son `bg-white` opacas, así que el contenido queda 100% legible — el patrón solo
   asoma en el lienzo de la página. `pattern: 'none'` (o tenant sin tema) = neutro.
   ============================================================================= */

export type ThemePattern = 'none' | 'waves' | 'drops' | 'flow' | 'mesh' | 'bits' | 'strong' | 'peace';

export interface WorkspaceTheme {
  pattern: ThemePattern | string;
  colors: string[];
}

/** Degradado firma de marca (default del picker si el tenant no eligió colores). */
export const DEFAULT_THEME_COLORS = ['#0D47A1', '#009688', '#43A047'];

/** Paleta de marca para el selector (quick-picks). */
export const BRAND_PALETTE = [
  '#0D47A1', '#1565C0', '#1976D2', '#1E88E5', '#2196F3', // azules
  '#1B5E20', '#2E7D32', '#388E3C', '#43A047', '#4CAF50', // verdes
  '#00796B', '#009688',                                   // teal puente
];

export const PATTERN_OPTIONS: Array<{ value: ThemePattern; label: string }> = [
  { value: 'none',   label: 'Ninguno (plano)' },
  { value: 'waves',  label: 'Ondas' },
  { value: 'drops',  label: 'Gotas (agua)' },
  { value: 'flow',   label: 'Flow / blobs' },
  { value: 'mesh',   label: 'Malla' },
  { value: 'bits',   label: 'Bits (tech)' },
  { value: 'strong', label: 'Strong (gym)' },
  { value: 'peace',  label: 'Peace (yoga)' },
];

/** Normaliza el theme: patrón válido + 1-3 colores hex (cae al default de marca). */
export function normalizeTheme(theme?: Partial<WorkspaceTheme> | null): WorkspaceTheme {
  const valid = PATTERN_OPTIONS.map((p) => p.value) as string[];
  const pattern = (theme?.pattern && valid.includes(theme.pattern)) ? theme.pattern : 'none';
  const colors = Array.isArray(theme?.colors)
    ? theme!.colors.filter((c) => /^#[0-9a-fA-F]{6}$/.test(c)).slice(0, 3)
    : [];
  return { pattern, colors } as WorkspaceTheme;
}

function rgba(hex: string, a: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return `rgba(15,23,42,${a})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

/** Devuelve siempre 3 colores (rellena con el default de marca si faltan). */
function tri(colors: string[]): [string, string, string] {
  const c = colors.length ? colors : DEFAULT_THEME_COLORS;
  return [c[0], c[1] ?? c[0], c[2] ?? c[1] ?? c[0]];
}

/** SVG de ondas (cover, sin tiles) data-URI — fills a baja alpha. */
function wavesImage(colors: string[]): string {
  const [a, b, c] = tri(colors);
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='1440' height='900' preserveAspectRatio='none' viewBox='0 0 1440 900'>` +
    `<path d='M0 200 C 320 120 540 280 720 220 S 1180 120 1440 200 V0 H0 Z' fill='${rgba(a, 0.10)}'/>` +
    `<path d='M0 460 C 360 380 560 540 760 470 S 1200 360 1440 450 V200 C 1180 120 720 220 720 220 S 320 120 0 200 Z' fill='${rgba(b, 0.08)}'/>` +
    `<path d='M0 900 C 360 760 600 880 820 800 S 1220 720 1440 820 V450 C 1200 360 760 470 760 470 S 360 380 0 460 Z' fill='${rgba(c, 0.07)}'/>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

// Gotas de agua PREMIUM: cuentas vítreas translúcidas con brillo especular + sombra
// de contacto, sobre el degradado firma. Distribución orgánica curada (NO retícula)
// en un lienzo 1440x900: cuentas grandes + medianas + satélites. [x, y, r, colorIdx 0-2]
const DROP_CIRCLES: Array<[number, number, number, number]> = [
  [180, 160, 36, 0], [1180, 240, 42, 1], [760, 640, 48, 2],
  [300, 420, 22, 2], [980, 120, 18, 0], [1320, 560, 26, 2],
  [540, 300, 16, 1], [120, 680, 24, 0], [640, 80, 14, 2],
  [880, 780, 22, 1], [240, 240, 8, 1], [200, 120, 5, 2],
  [1120, 300, 9, 0], [1240, 200, 5, 2], [700, 560, 10, 0],
  [820, 700, 6, 2], [430, 360, 8, 0], [560, 240, 5, 2],
  [1020, 600, 11, 1], [380, 760, 9, 2], [1300, 680, 6, 0],
  [90, 420, 10, 1], [660, 380, 5, 1], [900, 300, 13, 2],
  [1080, 760, 7, 0], [460, 600, 12, 1], [1380, 360, 9, 2],
];

/**
 * Gota de agua realista en SVG ligero (sin imágenes raster): cuerpo vítreo con
 * radial-gradient (claro arriba-izquierda → tinte de marca en el borde/menisco),
 * borde de luz, brillo especular y sombra de contacto difusa. Todo a baja opacidad
 * para que el contenido (cards `bg-white`) quede 100% legible encima.
 */
function dropsImage(colors: string[]): string {
  const [a, b, c] = tri(colors);
  const bodyGrad = (id: string, hex: string) =>
    `<radialGradient id='${id}' cx='38%' cy='30%' r='72%'>` +
      `<stop offset='0%' stop-color='#ffffff' stop-opacity='0.45'/>` +
      `<stop offset='34%' stop-color='${hex}' stop-opacity='0.05'/>` +
      `<stop offset='74%' stop-color='${hex}' stop-opacity='0.13'/>` +
      `<stop offset='100%' stop-color='${hex}' stop-opacity='0.28'/>` +
    `</radialGradient>`;
  const defs =
    `<defs>` +
      bodyGrad('g0', a) + bodyGrad('g1', b) + bodyGrad('g2', c) +
      `<radialGradient id='hl' cx='50%' cy='50%' r='50%'>` +
        `<stop offset='0%' stop-color='#ffffff' stop-opacity='0.9'/>` +
        `<stop offset='100%' stop-color='#ffffff' stop-opacity='0'/>` +
      `</radialGradient>` +
      `<filter id='sh' x='-60%' y='-60%' width='220%' height='220%'>` +
        `<feGaussianBlur stdDeviation='3.5'/>` +
      `</filter>` +
    `</defs>`;
  const beads = DROP_CIRCLES.map(([x, y, r, ci]) => {
    const hlr = Math.max(2, r * 0.42);
    return (
      // sombra de contacto (desplazada abajo-derecha, difusa)
      `<ellipse cx='${x + r * 0.1}' cy='${y + r * 0.55}' rx='${r * 0.82}' ry='${r * 0.42}' fill='rgba(13,38,60,0.10)' filter='url(#sh)'/>` +
      // cuerpo vítreo
      `<circle cx='${x}' cy='${y}' r='${r}' fill='url(#g${ci})'/>` +
      // borde de luz
      `<circle cx='${x}' cy='${y}' r='${r}' fill='none' stroke='rgba(255,255,255,0.22)' stroke-width='1'/>` +
      // brillo especular (catchlight) arriba-izquierda
      `<circle cx='${x - r * 0.3}' cy='${y - r * 0.34}' r='${hlr}' fill='url(#hl)'/>`
    );
  }).join('');
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='1440' height='900' viewBox='0 0 1440 900'>${defs}${beads}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/** Estilo de fondo por patrón. Devuelve `null` para 'none'. */
function backgroundStyle(theme: WorkspaceTheme): CSSProperties | null {
  const [a, b, c] = tri(theme.colors);
  switch (theme.pattern) {
    case 'flow':
      // Blobs suaves en 4 puntos. + presencia (feedback Arturo: estaba muy tenue):
      // alphas más altos y radios un poco más definidos, sin perder legibilidad.
      return {
        background:
          `radial-gradient(42rem 42rem at 10% 6%, ${rgba(a, 0.32)}, transparent 62%),` +
          `radial-gradient(40rem 40rem at 94% 16%, ${rgba(b, 0.30)}, transparent 62%),` +
          `radial-gradient(46rem 46rem at 72% 102%, ${rgba(c, 0.30)}, transparent 62%),` +
          `radial-gradient(26rem 26rem at 38% 64%, ${rgba(b, 0.18)}, transparent 60%)`,
      };
    case 'mesh':
      // Malla VISIBLE (feedback: se notaba aún menos que Flow): líneas de cuadrícula
      // reales (repeating-linear) + gradientes de esquina con más alpha.
      return {
        backgroundColor: rgba(a, 0.015),
        backgroundImage:
          `repeating-linear-gradient(0deg, ${rgba(a, 0.10)} 0 1px, transparent 1px 64px),` +
          `repeating-linear-gradient(90deg, ${rgba(a, 0.10)} 0 1px, transparent 1px 64px),` +
          `radial-gradient(34rem 34rem at 0% 0%, ${rgba(a, 0.24)}, transparent 58%),` +
          `radial-gradient(34rem 34rem at 100% 0%, ${rgba(b, 0.22)}, transparent 58%),` +
          `radial-gradient(34rem 34rem at 100% 100%, ${rgba(c, 0.24)}, transparent 58%),` +
          `radial-gradient(34rem 34rem at 0% 100%, ${rgba(b, 0.22)}, transparent 58%)`,
      };
    case 'drops':
      // Premium: cuentas de agua vítreas (SVG) SOBRE el degradado firma de marca
      // (azul → teal → verde) a baja alpha. Capa de gotas encima del degradado;
      // ambas detrás del contenido (cards opacas → 100% legible).
      return {
        backgroundColor: rgba(a, 0.02),
        backgroundImage:
          `${dropsImage(theme.colors)},` +
          `linear-gradient(135deg, ${rgba(a, 0.12)} 0%, ${rgba(b, 0.10)} 48%, ${rgba(c, 0.12)} 100%)`,
        backgroundSize: 'cover, cover',
        backgroundPosition: 'center, center',
        backgroundRepeat: 'no-repeat, no-repeat',
      };
    case 'waves':
      return {
        backgroundImage: wavesImage(theme.colors),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    case 'bits':
      // Tech/digital: retícula de "píxeles" (puntos) + líneas de circuito tenues.
      // Distribución regular pero con dos capas de puntos (color a y c) = "vida".
      return {
        backgroundColor: rgba(a, 0.02),
        backgroundImage:
          `repeating-linear-gradient(0deg, ${rgba(b, 0.07)} 0 1px, transparent 1px 44px),` +
          `repeating-linear-gradient(90deg, ${rgba(b, 0.07)} 0 1px, transparent 1px 44px),` +
          `radial-gradient(${rgba(a, 0.22)} 1.6px, transparent 2.4px),` +
          `radial-gradient(${rgba(c, 0.16)} 1.4px, transparent 2.2px)`,
        backgroundSize: '44px 44px, 44px 44px, 22px 22px, 22px 22px',
        backgroundPosition: '0 0, 0 0, 0 0, 11px 11px',
      };
    case 'strong':
      // Gym: diagonales enérgicas que se cruzan (chevron atlético). Más fuerte:
      // franjas marcadas a 45/-45° con buen alpha; el contenido sigue en cards.
      return {
        backgroundImage:
          `repeating-linear-gradient(45deg, ${rgba(a, 0.16)} 0 16px, transparent 16px 40px),` +
          `repeating-linear-gradient(-45deg, ${rgba(c, 0.13)} 0 12px, transparent 12px 44px),` +
          `linear-gradient(120deg, ${rgba(b, 0.14)}, transparent 55%)`,
      };
    case 'peace':
      // Yoga/mindfulness: ondas zen concéntricas muy suaves + wash gentil. Calmado.
      return {
        backgroundImage:
          `repeating-radial-gradient(circle at 50% 28%, transparent 0 40px, ${rgba(a, 0.06)} 40px 42px),` +
          `radial-gradient(60rem 60rem at 50% 28%, ${rgba(b, 0.16)}, transparent 70%),` +
          `radial-gradient(50rem 50rem at 50% 120%, ${rgba(c, 0.14)}, transparent 70%)`,
      };
    default:
      return null;
  }
}

/**
 * Capa de fondo del workspace.
 * - `preview=false` (default): fija a la ventana en `z-0`. El contenido del shell
 *   se monta en `relative z-10`, así el patrón asoma sobre el fondo neutro pero
 *   SIEMPRE queda detrás del contenido (cards `bg-white` opacas → 100% legible).
 * - `preview=true`: absoluta — llena su contenedor `relative` (para el preview en
 *   Configuración). Mismos estilos, así el preview es fiel a lo que se aplicará.
 */
export function WorkspaceBackground({
  theme, preview = false,
}: { theme?: Partial<WorkspaceTheme> | null; preview?: boolean }) {
  const style = backgroundStyle(normalizeTheme(theme));
  if (!style) return null; // 'none' → fondo neutro, sin capa.
  return (
    <div
      aria-hidden="true"
      className={preview ? 'absolute inset-0' : 'pointer-events-none fixed inset-0 z-0'}
      style={style}
    />
  );
}

export default WorkspaceBackground;
